import React, { useState, useEffect } from "react";
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useLocation, Redirect, useHistory } from "react-router-dom";

interface IDealersContext {
    dealers: IDealer[]
    recordCount: number
    search: ISearch
    isLoading: boolean

    doSearch: () => void
    handleChangePage: (event: any, page: number) => void
    downloadAsText: () => void
    downloadAsExcel: () => void
    deleteDealer: (id: string) => void
    updateSearch: (newSearch: ISearch) => void
}

export let dealersContext = React.createContext<IDealersContext>({} as IDealersContext);

let { Provider } = dealersContext;

interface IDealer {
    dealerId: string
    extId: string
    name: string
    address: string
    city: string
    zipCode: string
    state: string
    phone: string
    email: string
    website: string
    brands: string[]
    source: string
    isDealer: boolean
    isDistributor: boolean
    canManageDealer: boolean
    lng: number
    lat: number
    programs: IProgram[]
}

interface IProgram {
    name: string
    code: string
}

interface ISearch {
    searchText: string
    pageNr: number
    pageSize: number
    name: string
    countryCode: string
    m3Number: string
    isActive: string
    source: string
    brand: string
    program: string
}

let DealersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let history = useHistory();

    let [isLoading, setLoading] = useState(true);
    let [dealers, setDealers] = useState<IDealer[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        countryCode: '',
        isActive: 'true',
        m3Number: '',
        name: '',
        brand: '',
        pageNr: 0,
        pageSize: 20,
        program: '',
        searchText: '',
        source: ''
    });


    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch({
                ...search,
                ...state.search
            })
        }

        searchDealers({
            ...search,
            ...state.search
        });
    }

    let doSearch = () => {
        let request = {
            ...search,
            pageNr: search.pageNr > 0 ? 0 : search.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let downloadAsText = async () => {
        try {
            let response = await axios.post('/api/Dealer/DownloadDealersAsText', search, { responseType: 'blob' })
            let filename = "dealers.txt";
            let disposition = response.headers['content-disposition'];

            if (disposition && disposition.indexOf('attachment') !== -1) {
                let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                let matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1])
                    filename = matches[1].replace(/['"]/g, '');
            }

            let url = window.URL.createObjectURL(new Blob([response.data]));
            let link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        } catch (err) { }
    }

    let downloadAsExcel = async () => {
        try {
            let response = await axios.post('/api/Dealer/DownloadDealersAsExcel', search, { responseType: 'blob' })
            let filename = "dealers.xlsx";
            let disposition = response.headers['content-disposition'];

            if (disposition && disposition.indexOf('attachment') !== -1) {
                let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                let matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1])
                    filename = matches[1].replace(/['"]/g, '');
            }

            let url = window.URL.createObjectURL(new Blob([response.data]));
            let link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        } catch (err) { }
    }

    let searchDealers = async (values: ISearch) => {
        try {
            setLoading(true);
            var res = await axios.post('/api/Dealer/SearchDealers', values);

            setDealers(res.data.items);
            setTotalRecords(res.data.recordCount);
        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let deleteDealer = async (id: string) => {
        if (window.confirm("Are you sure that you want to delete this dealer?")) {
            try {
                await axios.delete(`/api/Dealer/DeleteDealer/${id}`)

                setDealers(dealers.filter(dealer => dealer.dealerId !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar('Dealer removed', { variant: 'success' });
            } catch (err) { }
        }
    }

    return (
        <Provider value={{
            isLoading,
            search,
            dealers,
            recordCount,

            doSearch,
            updateSearch,
            downloadAsText,
            downloadAsExcel,
            handleChangePage,
            deleteDealer
        }}>
            {children}
        </Provider>
    );
};

export default DealersProvider;